import { Grid, Typography } from "@mui/material";
import { usePoints } from "api/points";
import { Modals } from "components/Layout/Unit/UnitTransfersList/helpers";
import { UnitTransfersModals } from "components/Layout/Unit/UnitTransfersList/UnitTransfersModals";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useMemo, useState } from "react";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { UNITS_EARNED_FROM_CUSTOM_EVENT_ID } from "state/tables/types";
import { ListingActions } from "types/listing";
import { capitalizeFirstLetter, handleBooleanValue } from "utilities/strings";

interface Props {
  customEventId: string | undefined;
}

export const EarnedUnits = ({ customEventId }: Props) => {
  const { message } = useMessage();
  const { setColumnOrdering } = useTablesStateHelpers();
  const { listing, updateListing } = useListing(
    UNITS_EARNED_FROM_CUSTOM_EVENT_ID,
  );
  const { usePointsTransferList } = usePoints();

  const [selectedModal, setSelectedModal] = useState<{
    transfer: string;
    mode: number | null;
    isOpen: boolean;
  }>({
    transfer: "",
    mode: null,
    isOpen: false,
  });

  const { data: unitsTransfers } = usePointsTransferList({
    filters: customEventId
      ? [
          {
            condition: "eq",
            query: "actionCause:customEventId",
            value: customEventId,
          },
        ]
      : [],
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage || 25,
    sortKey: listing.property || "actionCause:transactionId",
  });

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "walletName" }),
          property: "walletName",
          isSortable: false,
        },
        {
          headerText: message({ id: "walletCode" }),
          property: "walletCode",
          isSortable: false,
        },
        {
          headerText: message({ id: "unit" }),
          property: "unit",
          isSortable: false,
        },
        {
          headerText: message({ id: "type" }),
          property: "type",
          isSortable: false,
        },
        {
          headerText: message({ id: "cancelled" }),
          property: "cancelled",
          isSortable: false,
        },
        {
          headerText: message({ id: "pending" }),
          property: "pending",
          isSortable: false,
        },
        {
          headerText: message({ id: "value" }),
          property: "value",
          emptyString: "0",
          align: "right",
          isSortable: false,
        },
        {
          headerText: message({ id: "comment" }),
          property: "comment",
          isSortable: false,
          dataHjSuppress: true,
        },
      ]),
    [message],
  );

  const itemsList = useMemo(
    () =>
      unitsTransfers?.items.map((item) => ({
        cells: [
          { content: item.walletType.name, property: "walletName" },
          { content: item.walletType.code, property: "walletCode" },
          { content: item.walletType.unitSingularName, property: "unit" },
          { content: capitalizeFirstLetter(item.type), property: "type" },
          {
            content: message({ id: handleBooleanValue(item.cancelled) }),
            property: "cancelled",
          },
          {
            content: message({ id: handleBooleanValue(item.pending) }),
            property: "pending",
          },
          { content: item.value, property: "value", align: "right" },
          { content: item.comment, property: "comment" },
        ],
        id: item.transferId,
        metadata: {
          cancelled: item.cancelled,
          pending: item.pending,
          type: item.type,
          expiresAt: item.expiresAt,
        },
        testId: "units-earned-values-list-row",
      })),
    [unitsTransfers?.items, message],
  );

  return (
    <Grid item xs={12}>
      <Typography marginBottom={6}>
        {message({ id: "unitsEarnedFromThisEvent" })}
      </Typography>
      <OLTable
        disableColumnManager
        setColumnOrdering={setColumnOrdering}
        tableName={UNITS_EARNED_FROM_CUSTOM_EVENT_ID}
        data-test-id="units-earned-values"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedModal({
                  transfer: id as string,
                  mode: Modals.Activate,
                  isOpen: true,
                });
              },
              hideCondition: (item: Row) =>
                item.metadata?.pending === false ||
                item.metadata?.cancelled === true,
              label: message({ id: "activate" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedModal({
                  transfer: id as string,
                  mode: Modals.Cancel,
                  isOpen: true,
                });
              },
              hideCondition: (item: Row) =>
                !["adding", "blocked"].includes(item.metadata?.type) ||
                item.metadata?.cancelled === true,
              label: message({ id: "cancel" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedModal({
                  transfer: id as string,
                  mode: Modals.Expire,
                  isOpen: true,
                });
              },
              hideCondition: (item: Row) =>
                !["adding", "p2p_adding"].includes(item.metadata?.type) ||
                item.metadata?.cancelled === true ||
                item.metadata?.pending === true ||
                !item.metadata?.expiresAt,
              label: message({ id: "expire" }),
            },
          ],
        }}
        columns={unitsTransfers && headItems}
        onSort={() => {}}
        rows={unitsTransfers && (itemsList as Row[])}
        sort={{
          order: "asc",
          property: "actionCause:transactionId",
          rowsPerPage: listing.rowsPerPage || 25,
          page: listing.page,
        }}
        pagination={{
          count: calculateCount(
            unitsTransfers?.total.filtered,
            unitsTransfers?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage || 25,
        }}
      />
      <UnitTransfersModals
        open={selectedModal.isOpen}
        selectedModal={{
          transfer: selectedModal.transfer,
          mode: selectedModal.mode as number,
        }}
        close={() =>
          setSelectedModal({ transfer: "", mode: null, isOpen: false })
        }
      />
    </Grid>
  );
};
